import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { STEP_CONFIRM, STEP_SELECT_TYPE } from '@constants/process-steps';
import { PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ImportFormData } from '@hooks/import/type';
import { useImportCsv } from '@hooks/import/use-import-csv';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { IMPORT_TYPE_CSV } from '@pages/events/utils/import-process-utils';

import { ImportCsvSteps } from './import-csv-steps';
import { ProcessImportCSVStep, SPLITTER_COMMA } from './process-import-csv.type';

/*
 * ProcessImportCsv Component
 *
 * Handles the CSV import process for invitations and other data.
 * Uses the shared process form hook for common functionality.
 */

export const ProcessImportCsv = () => {
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);

  // Use shared process form hook with proper typing
  const { methods, offer, eventId, closeModal }: UseProcessFormReturn = useProcessForm({
    additionalDefaults: {
      splitter: SPLITTER_COMMA,
    },
  });

  // Get additional context values needed for navigation
  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);

  const [step, setStep] = useState<ProcessImportCSVStep>(STEP_SELECT_TYPE);
  const [fileName, setFileName] = useState<string>('');
  const [rows, setRows] = useState<Array<string[]>>([]);
  const [localQrCodeIssues, setLocalQrCodeIssues] = useState<{ duplicateRows: number[]; emptyRows: number[] }>({
    duplicateRows: [],
    emptyRows: [],
  });
  const [localFormData, setLocalFormData] = useState<ImportFormData>();

  const { data: importCsvData, mutate: importCsv } = useImportCsv();

  const handleContinueReviewImport = (
    qrCodeIssues: {
      duplicateRows: number[];
      emptyRows: number[];
    },
    formData: ImportFormData
  ) => {
    setLocalQrCodeIssues(qrCodeIssues);
    setLocalFormData(formData);
    if (qrCodeIssues.duplicateRows.length || qrCodeIssues.emptyRows.length) {
      setStep(STEP_CONFIRM);
    } else if (localFormData) importCsv({ importData: localFormData });
  };

  const handleContinueConfirmImportCsv = () => {
    if (localFormData) importCsv({ importData: localFormData });
  };

  const createProcessPath = useCallback(
    (importId: string) => {
      if (!importId || !eventId) return;
      return PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importId)
        .replace(':importType', IMPORT_TYPE_CSV);
    },
    [companyLegacyId, clubSlug, eventId]
  );

  // Navigation effect is now handled by the shared hook's prevent exit logic

  useEffect(() => {
    if (importCsvData) {
      const path = createProcessPath(importCsvData.importId);
      if (path) navigate(path);
      closeModal();
    }
  }, [importCsvData, navigate, eventId, createProcessPath, closeModal]);

  return (
    <form className='flex justify-end flex-col relative' ref={formRef}>
      <FormProvider {...methods}>
        <ImportCsvSteps
          step={step}
          setStep={setStep}
          offer={offer}
          fileName={fileName}
          rows={rows}
          setRows={setRows}
          setFileName={setFileName}
          localQrCodeIssues={localQrCodeIssues}
          onContinueReviewImport={handleContinueReviewImport}
          onContinueConfirmImport={handleContinueConfirmImportCsv}
          onClose={closeModal}
        />
      </FormProvider>
    </form>
  );
};
